import { mergeDeepRight, mergeRight } from 'ramda';
import React, { createContext, useEffect, useReducer, useState } from 'react';
import { Domain, GeoLoc, LoadingStatus, LogementStatus, ModalStatus, Storage } from '../constants/constants';
import { getQueryParams } from '../utils/QueryParamsUtils';
import { Actions, StoreActionType } from './store.actions';
import { AppStoreModel } from './store.model';

interface IAppContext {
  apiStatus: LoadingStatus;
  geoLoc: GeoLoc;
  logementValue: LogementStatus;
  state: Partial<AppStoreModel>;
  modalStatus: ModalStatus;
  dispatch: React.Dispatch<Actions>;
  dispatchApiStatus: (newStatus: LoadingStatus) => void;
  dispatchGeoLoc: (newStatus: GeoLoc) => void;
  dispatchLogementValue: (newStatus: LogementStatus) => void;
  dispatchModalStatus: (newStatus: ModalStatus) => void;
  domain: string;
}

let lsStore = {};
try {
  lsStore = JSON.parse(sessionStorage.getItem(Storage.STORE_PP) ?? '');
} catch (err) {}
const initialState: Partial<AppStoreModel> = lsStore;

const store = createContext<IAppContext>({
  state: initialState,
  dispatch: () => null,
  apiStatus: LoadingStatus.END_ACTION,
  dispatchApiStatus: () => null,
  geoLoc: GeoLoc.NONE,
  dispatchGeoLoc: () => null,
  logementValue: LogementStatus.AUTRE,
  dispatchLogementValue: () => null,
  modalStatus: ModalStatus.CLOSED,
  dispatchModalStatus: () => null,
  domain: '',
});

const { Provider } = store;

const reducer = (state: Partial<AppStoreModel>, action: Actions): Partial<AppStoreModel> => {
  switch (action.type) {
    case StoreActionType.UPDATE: {
      return mergeDeepRight({ ...state }, { ...action.payload }) as Partial<AppStoreModel>;
    }
    case StoreActionType.REPLACE: {
      return mergeRight({ ...state }, { ...action.payload }) as Partial<AppStoreModel>;
    }
    case StoreActionType.CLEAR:
      return {};
    default:
      return state;
  }
};

const AppProvider = ({ children }: { children: React.JSX.Element }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [apiStatus, dispatchApiStatus] = useState<LoadingStatus>(LoadingStatus.END_ACTION);
  const [geoLoc, dispatchGeoLoc] = useState<GeoLoc>(GeoLoc.NONE);
  const [logementValue, dispatchLogementValue] = useState<LogementStatus>(LogementStatus.AUTRE);
  const [modalStatus, dispatchModalStatus] = useState<ModalStatus>(ModalStatus.CLOSED);
  const [domain] = useState<string>(window.location.hostname);
  const { geoLocation } = getQueryParams();

  useEffect(() => {
    sessionStorage.setItem(Storage.STORE_PP, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (domain.includes(Domain.CTLM) || domain.includes(Domain.LOCAL)) {
      dispatchGeoLoc(GeoLoc.CTLM);
    }

    if (domain.includes(Domain.CM)) {
      if (geoLocation === 'cmoi' || geoLocation === 'CMOI') {
        dispatchGeoLoc(GeoLoc.CMOI);
      }

      if (geoLocation === 'cmag' || geoLocation === 'CMAG') {
        dispatchGeoLoc(GeoLoc.CMAG);
      }
    }
  }, [domain]);

  return (
    <Provider
      value={{
        state,
        dispatch,
        apiStatus,
        dispatchApiStatus,
        modalStatus,
        dispatchModalStatus,
        geoLoc,
        dispatchGeoLoc,
        logementValue,
        dispatchLogementValue,
        domain,
      }}
    >
      {children}
    </Provider>
  );
};

export { store, AppProvider };
